import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatDate, formatTime, formatHours, formatCZK, formatCZKCompact } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ShiftReviewDialog } from "@/components/shared/ShiftReviewDialog";
import { ReviewPanel } from "@/components/shared/ReviewPanel";
import { useListNav, Kbd } from "@/components/shared/ListNav";
import { ApproveButton } from "@/components/shared/ApproveButton";
import { useIsMobile } from "@/components/shared/PagePrimitives";
import { celebrate } from "@/lib/celebrate";
import { BreakChips } from "@/components/shared/ShiftSections";
import { PresenceMap, haversineM } from "@/components/shared/PresenceMap";
import { SignedImg } from "@/components/shared/SignedImg";
import { PhotoLightbox } from "@/components/shared/PhotoLightbox";
import { PhotoPinViewer } from "@/components/defects/PhotoPinViewer";
import { DefectTaskForm } from "@/components/defects/DefectTaskForm";
import { Textarea } from "@/components/ui/textarea";
import { TimeField } from "@/components/shared/TimeField";
import { ClipboardCheck, Check, Camera, FileText, Package, Trash2, MapPin, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { autoPill } from "@/components/shared/Pill";

// Varovné štítky řádku fronty (161) — bez „edited" (to hromadné schválení nebrání).
function QueueFlags({ flags, hours }) {
  const f = flags || [];
  const chip = (k, label, cls) => <span key={k} data-testid={`queue-flag-${k}`} className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase rounded-md px-1.5 py-0.5 ${cls}`}>{label}</span>;
  const red = "text-destructive bg-destructive/10", amber = "text-amber-600 dark:text-amber-400 bg-amber-500/10", brand = "text-brand bg-brand/10";
  return (
    <>
      {f.includes("auto_closed") && chip("auto_closed", "auto odhlášeno", red)}
      {f.includes("no_gps") && chip("no_gps", "bez GPS", amber)}
      {f.includes("short") && chip("short", `směna ${Math.round((hours || 0) * 60)} min`, red)}
      {f.includes("out_of_radius") && chip("out_of_radius", "mimo okruh", amber)}
      {f.includes("no_photos") && chip("no_photos", "bez fotek", amber)}
      {f.includes("edited") && chip("edited", "upraveno", brand)}
    </>
  );
}

function RowIndicators({ r }) {
  return (
    <span className="inline-flex items-center gap-2 text-[12px] text-muted-foreground tabular-nums">
      {r.photos_count > 0 && <span className="inline-flex items-center gap-0.5" data-testid={`ind-photos-${r.id}`}><Camera size={13} /> {r.photos_count}</span>}
      {r.has_report && <span data-testid={`ind-report-${r.id}`}><FileText size={13} /></span>}
      {r.has_materials && <span data-testid={`ind-materials-${r.id}`}><Package size={13} /></span>}
    </span>
  );
}

// JEDNA fronta pro segmenty docházky (píchačka) i denní výkazy. Klik na řádek =
// Kontrola směny / úprava výkazu (oba typy stejně, bez zvláštní tužky);
// fajfka = schválit rovnou. „Schválit vše" má chytrý filtr (161).
// Mobilní karta „Docházka k odsouhlasení“ (196) — druhý slajd karty směny na přehledu
// vedení. Stejná data, schvalování i dialogy jako fronta, jen jiný vzhled.
const AV_COLORS = ["#2a8467", "#2f6f9f", "#7a5bb5", "#b4532a", "#3f7f7a", "#8a6a2f", "#5b6bb5", "#4f7f3a", "#a0527a", "#2f7f9f", "#9a6a2a", "#6b6f7a"];
const avColor = (key) => { let h = 0; for (const c of String(key || "")) h = (h * 31 + c.charCodeAt(0)) >>> 0; return AV_COLORS[h % AV_COLORS.length]; };
const initials = (name) => String(name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const hmShort = (iso) => (iso ? formatTime(iso).replace(/^0(\d)/, "$1") : "…");
const decH = (h) => `${String(Math.round((Number(h) || 0) * 10) / 10).replace(".", ",")} h`;
const word = (n) => (n === 1 ? "záznam" : n >= 2 && n <= 4 ? "záznamy" : "záznamů");
const pragueToday = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });
function dayHead(date, today) {
  const ymd = (s) => { const [y, m, d] = String(s || "").slice(0, 10).split("-").map(Number); return Date.UTC(y, (m || 1) - 1, d || 1); };
  const n = Math.round((ymd(today) - ymd(date)) / 86400000);
  if (n === 0) return "dnes";
  if (n === 1) return "včera";
  const dt = new Date(ymd(date));
  return `${dt.toLocaleDateString("cs-CZ", { weekday: "long", timeZone: "UTC" })} ${dt.getUTCDate()}. ${dt.getUTCMonth() + 1}.`;
}
const SEG_FLAGS = { auto_closed: ["auto odhlášeno", "r"], no_gps: ["bez GPS", "a"], out_of_radius: ["mimo okruh", "a"], no_photos: ["bez fotek", "a"], edited: ["upraveno", "b"],
  self_declared: ["konec doplnil pracovník", "b"], overtime_beyond_auto: ["přesčas nad rámec", "r"] };
const CHIP = { r: "pp-chip-r", a: "pp-chip-a", b: "pp-chip-b" };

export default function ApprovalQueue({ enrich = null, canSeeMoney = false, onChanged, variant = "list", expanded = false,
  onExpandedChange, highlightId = null, onCount, reloadKey = 0, onOpenAll, keys = false } = {}) {
  const [rows, setRows] = useState(null);
  const [days, setDays] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [reviewSid, setReviewSid] = useState(null);   // otevřená Kontrola směny
  const [confirmAll, setConfirmAll] = useState(false);
  const [onlyClean, setOnlyClean] = useState(true);
  const [radius, setRadius] = useState(300);
  const [dayTab, setDayTab] = useState("shift");
  const [lightbox, setLightbox] = useState(null);
  const [del, setDel] = useState(null);   // 179: { kind: "seg" | "day", id, label }
  const runDelete = async () => {
    const d = del; setDel(null); setBusy(true);
    try {
      await api.delete(d.kind === "day" ? `/daysheet/${d.id}` : `/attendance/${d.id}`);
      toast.success("Záznam smazán.");
      if (dayEdit?.id === d.id) setDayEdit(null);
      drop(d.kind, d.id);
      after();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const trash = (d, size = 8) => (
    <button type="button" onClick={(e) => { e.stopPropagation(); setDel(d); }} disabled={busy} title="Smazat záznam" aria-label="Smazat záznam"
      data-testid={`approval-del-${d.id}`}
      className={`inline-flex items-center justify-center ${size === 8 ? "h-8 w-8" : "h-9 w-9"} rounded-[8px] border border-border text-muted-foreground hover:text-destructive hover:border-destructive/50 shrink-0`}><Trash2 size={15} /></button>
  );

  const load = useCallback(() => {
    api.get("/attendance/pending").then((r) => setRows(r.data.rows || [])).catch(() => setRows([]));
    api.get("/daysheet/pending").then((r) => { setDays(r.data.items || []); setRadius(r.data.radius_m || 300); }).catch(() => setDays([]));
  }, []);
  useEffect(() => { load(); }, [load, reloadKey]);
  useEffect(() => { if (rows !== null && days !== null) onCount?.(rows.length + days.length); }, [rows, days, onCount]);
  // 219: vyřízený řádek zmizí hned (bez čekání na server) a obě fronty i rodič
  // (Přehled) se obnoví jednou po krátké pauze — ne po každém kliknutí zvlášť.
  const reloadTimer = useRef(null);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  useEffect(() => () => clearTimeout(reloadTimer.current), []);
  const refreshNow = useCallback(() => { clearTimeout(reloadTimer.current); load(); onChangedRef.current?.(); }, [load]);
  const after = useCallback(() => {
    clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => { load(); onChangedRef.current?.(); }, 800);
  }, [load]);
  const drop = (kind, id) => (kind === "day"
    ? setDays((ds) => (ds ? ds.filter((x) => x.id !== id) : ds))
    : setRows((rs) => (rs ? rs.filter((x) => x.id !== id) : rs)));
  // 358: na počítači se v panelu schvaluje vlevo nahoře a panel zůstane otevřený. Co se v něm schválilo,
  // zůstane ve frontě označené „Schváleno“ (šipky i vrácení schválení fungují dál) a fronta se obnoví
  // až po zavření panelu. Na telefonu se okno po schválení zavře jako dřív.
  const mobile = useIsMobile();
  const [sessionOk, setSessionOk] = useState({});   // id → { kind }
  const dayRows = useRef({});                         // id výkazu → zakázky ze serveru se schválením (pro návrat šipkou)
  const queueStale = useRef(false);
  const markOk = (id, kind, ok) => setSessionOk((m) => { const n = { ...m }; if (ok) n[id] = { kind }; else delete n[id]; return n; });
  const panelChanged = () => { queueStale.current = true; };

  const segCost = (r) => (r.cost != null ? r.cost : enrich?.[r.id]?.cost);
  const approveSeg = async (id) => {
    setBusy(true);
    try { await api.post(`/attendance/${id}/approve`, {}); toast.success("Docházka schválena."); drop("seg", id); after(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const approveDay = async (id) => {
    setBusy(true);
    try { await api.post(`/daysheet/${id}/approve`, {}); toast.success("Výkaz schválen."); drop("day", id); after(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const hhmm = (iso) => iso ? new Intl.DateTimeFormat("cs-CZ", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Prague" }).format(new Date(iso)) : "";
  const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
  const pad5 = (t) => (t || "").padStart(5, "0");
  const [dayEdit, setDayEdit] = useState(null);
  const openDayEdit = (it) => {
    let cursor = pad5(hhmm(it.startedAt));
    const dr = (it.allocations || []).map((a) => {
      const from = a.t_from ? pad5(a.t_from) : cursor;
      const to = a.t_to ? pad5(a.t_to)
        : `${String(Math.floor((toMin(from) + Math.round((a.hours || 0) * 60)) / 60)).padStart(2, "0")}:${String((toMin(from) + Math.round((a.hours || 0) * 60)) % 60).padStart(2, "0")}`;
      cursor = to;
      return { ...a, t_from: from, t_to: to, break_minutes: a.break_minutes || 0, reason: "" };
    });
    const saved = sessionOk[it.id] && dayRows.current[it.id];
    if (saved) dr.forEach((r, i) => { r.approval = saved[i]?.approval || null; });
    setRowDrafts({}); setPinView(null);
    setDayTab("shift");
    setDayEdit({ id: it.id, userId: it.userId, userName: it.userName, date: it.date, startedAt: it.startedAt, endedAt: it.endedAt,
      trackedHours: it.trackedHours, rows: dr, orig: JSON.stringify(dr.map(rowKey)), break_minutes: it.breakMinutes ?? 0, note: "",
      geo: it.geo || {}, sites: it.sites || [], autoClosed: it.autoClosed });
  };
  const rowKey = (r) => [r.t_from, r.t_to, String(Number(r.break_minutes) || 0), (r.note || "").trim()];
  const dayDirty = !!dayEdit && JSON.stringify(dayEdit.rows.map(rowKey)) !== dayEdit.orig;
  // 180: každá zakázka výkazu se schvaluje zvlášť, s vlastním důvodem úpravy.
  const rowDirty = (i) => !!dayEdit && JSON.stringify(rowKey(dayEdit.rows[i])) !== JSON.stringify(JSON.parse(dayEdit.orig)[i]);
  const rowPayload = (r, i) => ({ index: i, t_from: r.t_from, t_to: r.t_to, break_minutes: Number(r.break_minutes) || 0,
    note: r.note || "", reason: (r.reason || "").trim() || null });
  const approveRow = async (i, quiet) => {
    const r = dayEdit.rows[i];
    if (rowDirty(i) && !(r.reason || "").trim()) { toast.error(`Uveďte důvod úpravy u zakázky ${r.projectName || "Bez zakázky"}.`); return null; }
    if (toMin(r.t_to) <= toMin(r.t_from)) { toast.error("Konec musí být po začátku."); return null; }
    const { data } = await api.post(`/daysheet/${dayEdit.id}/approve-row`, rowPayload(r, i));
    dayRows.current[dayEdit.id] = data.day.allocations || [];
    const approval = data.day.allocations?.[i]?.approval;
    setDayEdit((d) => d && ({ ...d, rows: d.rows.map((x, k) => (k === i ? { ...x, approval } : x)),
      orig: JSON.stringify(d.rows.map((x, k) => (k === i ? rowKey(x) : JSON.parse(d.orig)[k]))) }));
    if (!quiet) toast.success(data.done ? "Výkaz schválen." : `Schváleno: ${r.projectName || "Bez zakázky"}.`);
    return data;
  };
  const approveRowUi = async (i) => {
    setBusy(true);
    try {
      const d = await approveRow(i);
      if (d?.done && !mobile) { markOk(dayEdit.id, "day", true); panelChanged(); }
      else if (d?.done) { drop("day", dayEdit.id); setDayEdit(null); after(); } else if (d) after();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const [rowDrafts, setRowDraftsMap] = useState({});
  const [pinView, setPinView] = useState(null);   // { row, start, focusDraft }
  const setRowDrafts = (m) => setRowDraftsMap(m);
  const draftsSetter = (i) => (v) => setRowDraftsMap((m) => ({ ...m, [i]: typeof v === "function" ? v(m[i] || []) : v }));
  // stay (358, panel vlevo nahoře): panel zůstane otevřený, tlačítko zezelená a vyletí konfety
  const submitDayEdit = async (e, stay = false) => {
    const from = e?.currentTarget;
    const pending = dayEdit.rows.map((r, i) => i).filter((i) => !dayEdit.rows[i].approval);
    const missing = pending.find((i) => rowDirty(i) && !(dayEdit.rows[i].reason || "").trim());
    if (missing != null) return toast.error(`Uveďte důvod úpravy u zakázky ${dayEdit.rows[missing].projectName || "Bez zakázky"}.`);
    setBusy(true);
    try {
      for (const i of pending) { const d = await approveRow(i, true); if (!d) return; }
      if (stay) { celebrate(from); markOk(dayEdit.id, "day", true); panelChanged(); return; }
      toast.success(dayDirty ? "Výkaz upraven a schválen." : "Výkaz schválen."); drop("day", dayEdit.id); setDayEdit(null); after();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); after(); }
    finally { setBusy(false); }
  };


  // 358: schváleno omylem — druhé klepnutí na „Schváleno" vrátí výkaz ke schválení
  const unapproveDay = async () => {
    setBusy(true);
    try {
      await api.post(`/daysheet/${dayEdit.id}/unapprove`);
      setDayEdit((d) => d && { ...d, rows: d.rows.map((r) => ({ ...r, approval: null })) });
      markOk(dayEdit.id, "day", false); panelChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  // po zavření panelu: schválené zmizí z fronty a fronta i Přehled se obnoví
  useEffect(() => {
    if (reviewSid || dayEdit || !queueStale.current) return;
    queueStale.current = false;
    Object.entries(sessionOk).forEach(([id, v]) => drop(v.kind, id));
    setSessionOk({});
    after();
  }, [reviewSid, dayEdit]);   // eslint-disable-line react-hooks/exhaustive-deps

  // 219: výkazy jedním requestem (dřív POST za POSTem) — stejná pravidla na serveru,
  // včetně „jen bez varování“ (auto odhlášení, přesčas); obě fronty souběžně.
  const runApproveAll = async () => {
    setConfirmAll(false); setBusy(true);
    const segIds = rows.map((r) => r.id);
    const dayIds = days.map((d) => d.id);
    try {
      const [segRes, dayRes] = await Promise.all([
        segIds.length ? api.post("/attendance/approve-bulk", { ids: segIds, only_clean: onlyClean }) : null,
        dayIds.length ? api.post("/daysheet/approve-bulk", { ids: dayIds, only_clean: onlyClean }) : null,
      ]);
      const skipped = (segRes?.data?.skipped || 0) + (dayRes?.data?.skipped || 0);
      if (onlyClean && skipped > 0) {
        toast.success("Schváleny záznamy bez varování. Zbytek čeká na kontrolu.");
      } else {
        toast.success("Vše schváleno.");
      }
      refreshNow();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); refreshNow(); }
    finally { setBusy(false); }
  };

  const R = rows || [], D = days || [];
  const total = R.length + D.length;
  const totalHours = R.reduce((a, r) => a + (r.hours || 0), 0) + D.reduce((a, d) => a + (d.trackedHours || 0), 0);
  const totalCost = R.reduce((a, r) => a + (Number(segCost(r)) || 0), 0) + D.reduce((a, d) => a + (Number(d.cost) || 0), 0);
  const showCost = canSeeMoney && (R.some((r) => segCost(r) != null) || D.some((d) => d.cost != null));
  const segWarn = (r) => (r.flags || []).some((f) => f !== "edited");
  const dayWarn = (d) => d.autoClosed || d.balance === "overtime";
  const warnCount = R.filter(segWarn).length + D.filter(dayWarn).length;
  const plural = (n) => (n === 1 ? "záznam" : n < 5 ? "záznamy" : "záznamů");
  // bez varování = to, co schválí „Schválit vše“ s volbou jen čisté (server i výkazy)
  const cleanSegs = R.filter((r) => !segWarn(r)), cleanDays = D.filter((d) => !dayWarn(d));
  const cleanCount = cleanSegs.length + cleanDays.length;
  const cleanHours = cleanSegs.reduce((a, r) => a + (r.hours || 0), 0) + cleanDays.reduce((a, d) => a + (d.trackedHours || 0), 0);
  const cleanCost = cleanSegs.reduce((a, r) => a + (Number(segCost(r)) || 0), 0) + cleanDays.reduce((a, d) => a + (Number(d.cost) || 0), 0);
  const pass = variant === "pass";

  // ---- mobilní karta (196) ----
  const today = pragueToday();
  const items = [
    ...R.map((r) => ({ id: r.id, kind: "seg", open: () => setReviewSid(r.id), approve: () => approveSeg(r.id), date: r.date, name: r.user_name,
      key: r.user_id || r.user_name, sub: `${r.project_name || "Bez zakázky"} · ${hmShort(r.check_in)}–${hmShort(r.check_out)}`,
      proj: r.project_name || "Bez zakázky", time: `${hmShort(r.check_in)}–${hmShort(r.check_out)}`,
      hours: r.hours, cost: segCost(r), sort: r.check_out || r.check_in || "",
      flags: (r.flags || []).map((f) => (f === "short" ? [`směna ${Math.round((r.hours || 0) * 60)} min`, "r"] : SEG_FLAGS[f])).filter(Boolean) })),
    ...D.map((d) => ({ id: d.id, kind: "day", open: () => openDayEdit(d), approve: () => approveDay(d.id), date: d.date, name: d.userName,
      key: d.userId || d.userName, sub: `${(d.allocations || []).map((a) => a.projectName || "Bez zakázky").join(" · ") || "Denní výkaz"} · ${hmShort(d.startedAt)}–${hmShort(d.endedAt)}`,
      proj: (d.allocations || []).map((a) => a.projectName || "Bez zakázky").join(" · ") || "Denní výkaz", time: `${hmShort(d.startedAt)}–${hmShort(d.endedAt)}`,
      hours: d.trackedHours, cost: d.cost, sort: d.endedAt || d.startedAt || "",
      flags: [d.autoClosed && ["auto odhlášeno", "r"], !d.geo?.start && ["bez GPS", "a"], d.balance === "overtime" && ["přesčas", "b"]].filter(Boolean) })),
  ].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || String(b.sort).localeCompare(String(a.sort)));
  const passCard = (
    <div className="pp-pass rounded-[26px] px-5 py-[18px] flex flex-col gap-3.5 h-full" role="group" aria-label="Docházka k odsouhlasení" data-testid="approval-pass">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-white/80">Docházka k odsouhlasení</span>
        {total > 0 && warnCount > 0 && <span className="text-xs font-bold rounded-xl px-2.5 py-1 bg-brand text-white whitespace-nowrap" data-testid="approval-pass-warn">{warnCount} s varováním</span>}
      </div>
      {rows === null || days === null ? (
        <div className="h-24 rounded-2xl bg-white/10 animate-pulse motion-reduce:animate-none" />
      ) : total === 0 ? (
        <div role="status" className="flex items-center gap-3 py-1" data-testid="approval-pass-empty">
          <span className="w-9 h-9 rounded-full bg-success text-white grid place-items-center shrink-0"><Check size={18} strokeWidth={2.6} aria-hidden="true" /></span>
          <span className="min-w-0"><span className="block text-base font-bold">Vše schváleno</span><span className="block text-[13px] text-white/70">Nic nečeká na odsouhlasení</span></span>
        </div>
      ) : (<>
        <div>
          <div className="font-heading tabular-nums whitespace-nowrap leading-none"><span className="text-[56px] font-extrabold" data-testid="approval-pass-count">{total}</span><span className="text-xl font-bold text-white/80"> {word(total)}</span></div>
          <div className="mt-2 text-sm text-white/80 tabular-nums truncate">{decH(totalHours)}{showCost ? ` · ${formatCZKCompact(totalCost)}` : ""}</div>
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
          {cleanCount > 0 ? (
            <button type="button" onClick={() => { setOnlyClean(true); setConfirmAll(true); }} disabled={busy} data-testid="approval-pass-all"
              className="h-12 rounded-full bg-success text-white text-sm font-bold inline-flex items-center justify-center gap-1.5 px-3.5 min-w-0 whitespace-nowrap active:scale-[.97] transition-transform motion-reduce:transition-none">
              <Check size={16} strokeWidth={2.6} aria-hidden="true" /><span className="truncate">{warnCount ? `Schválit ${cleanCount} bez varování` : `Schválit vše (${cleanCount})`}</span>
            </button>
          ) : <span />}
          <button type="button" onClick={() => onExpandedChange?.(!expanded)} aria-expanded={expanded} data-testid="approval-pass-toggle"
            className="h-12 rounded-full bg-white/[.12] text-white text-sm font-bold inline-flex items-center justify-center gap-1.5 px-4 whitespace-nowrap active:scale-[.97] transition-transform motion-reduce:transition-none">
            {expanded ? "Sbalit" : "Zobrazit"}<ChevronDown size={18} aria-hidden="true" className={`transition-transform motion-reduce:transition-none ${expanded ? "rotate-180" : ""}`} />
          </button>
        </div>
        {expanded && (
          <div className="-mx-1.5 -mt-1" data-testid="approval-pass-list">
            {items.map((it, i) => {
              const head = i === 0 || items[i - 1].date !== it.date;
              const hi = highlightId === it.id;
              return (
                <div key={it.id}>
                  {head && <div className="text-xs font-bold uppercase tracking-[.06em] text-white/70 pt-3 pb-0.5 px-0.5">{dayHead(it.date, today)}</div>}
                  <div className={`flex items-center gap-2 transition-colors duration-500 motion-reduce:transition-none ${head ? "" : "border-t border-white/15"} ${hi ? "bg-brand/25 rounded-xl" : ""}`} data-testid={`approval-pass-row-${it.id}`}>
                    <button type="button" onClick={it.open} className="flex-1 min-w-0 flex items-center gap-2.5 min-h-[60px] py-2 pl-1 text-left"
                      aria-label={`${it.name}, ${dayHead(it.date, today)}, ${it.sub}, ${decH(it.hours)}${it.flags.length ? `, ${it.flags.map((f) => f[0]).join(", ")}` : ""} — otevřít kontrolu`}>
                      <span className="w-[34px] h-[34px] rounded-full grid place-items-center text-xs font-bold text-white shrink-0" style={{ background: avColor(it.key) }}>{initials(it.name)}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[15px] font-semibold truncate">{it.name}</span>
                        <span className="block text-[13px] text-white/70 truncate tabular-nums">{it.sub}</span>
                        {it.flags.length > 0 && <span className="flex flex-wrap gap-1 mt-1">{it.flags.map(([label, c]) => <span key={label} className={`text-[11px] font-bold rounded-md px-1.5 py-0.5 whitespace-nowrap ${CHIP[c]}`}>{label}</span>)}</span>}
                      </span>
                      <span className="text-right whitespace-nowrap shrink-0 tabular-nums">
                        <span className="block text-[15px] font-bold">{decH(it.hours)}</span>
                        {showCost && it.cost != null && <span className="block text-xs text-white/70">{formatCZK(it.cost)}</span>}
                      </span>
                    </button>
                    <button type="button" onClick={it.approve} disabled={busy} aria-label={`Schválit — ${it.name}, ${dayHead(it.date, today)}`} data-testid={`approval-pass-ok-${it.id}`}
                      className="w-11 h-11 rounded-full bg-success text-white grid place-items-center shrink-0 mr-1 disabled:opacity-60"><Check size={18} strokeWidth={2.6} aria-hidden="true" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>)}
    </div>
  );
  // ---- pruh na úvodu počítače (236): posuvný seznam po dnech, bez „Schválit vše“ ----
  const bandItems = [...items].sort((a, b) => String(b.date || "").localeCompare(String(a.date || ""))
    || (b.flags.length > 0) - (a.flags.length > 0) || String(b.sort).localeCompare(String(a.sort)));
  const bandHead = (date) => {
    const n = dayHead(date, today);
    const [y, m, d] = String(date || "").slice(0, 10).split("-").map(Number);
    const full = y ? `${new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("cs-CZ", { weekday: "long", timeZone: "UTC" })} ${d}. ${m}.` : "";
    return n === "dnes" ? `Dnes · ${full}` : n === "včera" ? `Včera · ${full}` : full;
  };
  // 355: pruh na Přehledu klávesnicí jako Co hoří — ↑ ↓ / najetí myší vybere, mezerník otevře kontrolu.
  // V otevřeném panelu ↑ ↓ listují frontou v pořadí, v jakém jde na obrazovce.
  const kb = useListNav("att", bandItems.length, (i) => bandItems[i]?.open(), { enabled: variant === "band" && keys });
  const navList = variant === "band" ? bandItems : items;
  const openIdx = reviewSid ? navList.findIndex((x) => x.kind === "seg" && x.id === reviewSid)
    : dayEdit ? navList.findIndex((x) => x.kind === "day" && x.id === dayEdit.id) : -1;
  const goTo = (i) => {
    const it = navList[i];
    if (!it) return;
    if (it.kind === "seg") { setDayEdit(null); setReviewSid(it.id); } else { setReviewSid(null); it.open(); }
    if (variant === "band") kb.setSel(i);
  };
  const nav = openIdx >= 0 ? { index: openIdx, count: navList.length, onGo: goTo } : null;
  const bandPanel = (
    <div className="pp-band-panel rounded-[18px] px-4 pt-3.5 pb-2.5 min-w-0" role="group" aria-label="Docházka ke schválení" data-testid="approval-band">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading text-lg font-bold flex items-center gap-2 min-w-0">
          <span className="truncate">Docházka ke schválení</span>
          {total > 0 && <span className="pp-band-cnt" data-testid="approval-band-count">{total}</span>}
        </h3>
        {onOpenAll && <button type="button" onClick={onOpenAll} className="pp-band-link shrink-0" data-testid="approval-band-open">Otevřít v Docházce</button>}
      </div>
      {rows === null || days === null ? (
        <div className="h-28 mt-3 rounded-2xl bg-white/10 animate-pulse motion-reduce:animate-none" />
      ) : total === 0 ? (
        <div role="status" className="flex items-center gap-3 py-3" data-testid="approval-band-empty">
          <span className="w-9 h-9 rounded-full bg-success text-white grid place-items-center shrink-0"><Check size={18} strokeWidth={2.6} aria-hidden="true" /></span>
          <span className="text-[15px] font-bold">Vše schváleno</span>
        </div>
      ) : (<>
        <div className="flex items-center gap-3 mt-0.5">
          <p className="text-[13px] text-white/75 tabular-nums truncate min-w-0">
            {total} {word(total)} · {decH(totalHours)}{warnCount ? ` · ${warnCount} s varováním` : ""}
          </p>
          {keys && (   // nápověda kláves jen na počítači
            <span className="ml-auto shrink-0 hidden xl:flex items-center gap-1.5 text-[12px] text-white/75 whitespace-nowrap" data-testid="approval-band-keys">
              <Kbd className="!bg-white/15 !border-white/25 !text-white">↑</Kbd><Kbd className="!bg-white/15 !border-white/25 !text-white">↓</Kbd> vybrat
              <Kbd className="!bg-white/15 !border-white/25 !text-white">␣</Kbd> otevřít
            </span>
          )}
        </div>
        <div className="pp-band-scroll mt-2 max-h-[300px] overflow-y-auto -mx-2" data-testid="approval-band-list">
          {bandItems.map((it, i) => {
            const head = i === 0 || bandItems[i - 1].date !== it.date;
            const n = head ? bandItems.filter((x) => x.date === it.date).length : 0;
            return (
              <div key={it.id}>
                {head && (
                  <div className="sticky top-0 z-[1] flex justify-between gap-2 px-2 text-[11.5px] font-extrabold uppercase tracking-[.06em] text-white/75 pt-2 pb-1" style={{ background: "var(--band-sticky)" }}>
                    <span className="truncate">{bandHead(it.date)}</span><span className="tabular-nums">{n}</span>
                  </div>
                )}
                {/* celý řádek otevře kontrolu; tlačítko se jménem je pro Tab a čtečku (klik probublá na řádek) */}
                <div {...kb.rowProps(i)} tabIndex={-1} onClick={() => { kb.setSel(i); it.open(); }} data-testid={`approval-band-row-${it.id}`}
                  className={`flex items-center gap-2.5 px-2 py-2 scroll-mt-9 rounded-[8px] border-b border-white/[.12] cursor-pointer outline-none ${kb.sel === i ? "bg-white/[.12]" : ""}`}>
                  <button type="button" className="flex-1 min-w-0 text-left"
                    aria-label={`${it.name}, ${dayHead(it.date, today)}, ${it.sub}, ${decH(it.hours)}${it.flags.length ? `, ${it.flags.map((f) => f[0]).join(", ")}` : ""} — otevřít kontrolu`}>
                    <span className="block text-sm font-bold truncate">{it.name} <span className="font-medium text-white/75">· {it.proj}</span></span>
                    <span className="flex flex-wrap items-center gap-1.5 mt-0.5 text-xs text-white/75 tabular-nums">
                      {it.flags.map(([label, c]) => <span key={label} className={`text-[10.5px] font-extrabold uppercase tracking-[.04em] rounded-md px-1.5 py-px whitespace-nowrap ${CHIP[c]}`}>{label}</span>)}
                      <span>{it.time}</span>
                    </span>
                  </button>
                  <span className="font-heading text-[15px] font-extrabold tabular-nums whitespace-nowrap">{decH(it.hours)}</span>
                  {showCost && (
                    <span className="text-xs text-white/75 tabular-nums whitespace-nowrap min-w-[64px] text-right">
                      {Number(it.cost) > 0 ? formatCZK(it.cost) : <span title="Pracovník nemá nastavenou hodinovou sazbu">bez sazby</span>}
                    </span>
                  )}
                  {sessionOk[it.id] ? (
                    <span className="inline-flex items-center gap-1 h-[30px] px-2 rounded-[10px] bg-success/90 text-white text-[12px] font-extrabold shrink-0" data-testid={`approval-band-done-${it.id}`}>
                      <Check size={13} strokeWidth={2.8} aria-hidden="true" />Schváleno
                    </span>
                  ) : (
                    <button type="button" onClick={(e) => { e.stopPropagation(); it.approve(); }} disabled={busy} title="Schválit" aria-label={`Schválit — ${it.name}, ${dayHead(it.date, today)}`} data-testid={`approval-band-ok-${it.id}`}
                      className="w-[30px] h-[30px] rounded-[10px] bg-success text-white grid place-items-center shrink-0 disabled:opacity-60 hover:brightness-110"><Check size={15} strokeWidth={2.6} aria-hidden="true" /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {total > 5 && (
          <div className="text-[12.5px] text-white/70 pt-2">
            Posouvejte seznamem{onOpenAll ? <>, nebo <button type="button" onClick={onOpenAll} className="underline underline-offset-2 text-white">otevřete Docházku</button></> : null}
          </div>
        )}
      </>)}
    </div>
  );
  const dialogs = (
    <>
      {/* Schválit vše */}
      <AlertDialog open={confirmAll} onOpenChange={(o) => { if (!o) setConfirmAll(false); }}>
        <AlertDialogContent data-testid="approve-all-dialog">
          {pass ? (
            <AlertDialogHeader>
              <AlertDialogTitle>Schválit {cleanCount} {word(cleanCount)}?</AlertDialogTitle>
              <AlertDialogDescription className="tabular-nums">{formatHours(cleanHours)}{showCost ? ` · ${formatCZK(cleanCost)}` : ""}</AlertDialogDescription>
              {warnCount > 0 && (
                <p className="rounded-[14px] px-3.5 py-3 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm font-semibold text-left" data-testid="approve-all-warn-note">
                  {warnCount} {word(warnCount)} s varováním {warnCount === 1 ? "zůstane" : "zůstanou"} ke kontrole — schválíš je po jednom.
                </p>
              )}
            </AlertDialogHeader>
          ) : (<>
          <AlertDialogHeader>
            <AlertDialogTitle>Schválit vše?</AlertDialogTitle>
            <AlertDialogDescription className="tabular-nums">{total} {plural(total)} · {formatHours(totalHours)}{showCost ? ` · ${formatCZK(totalCost)}` : ""}</AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-start gap-2.5 cursor-pointer text-sm font-semibold rounded-[12px] border border-border p-3" htmlFor="approve-only-clean">
            <Checkbox id="approve-only-clean" checked={onlyClean} onCheckedChange={(v) => setOnlyClean(!!v)} data-testid="approve-only-clean" className="mt-0.5" />
            Schválit jen záznamy bez varování, ostatní nechat k ruční kontrole
          </label>
          </>)}
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setConfirmAll(false)} className="h-11 w-full sm:w-auto">Zpět</Button>
            <Button onClick={runApproveAll} disabled={busy} data-testid="approve-all-confirm" className="h-11 w-full sm:w-auto font-bold bg-success hover:bg-success/90 text-white">Schválit</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Schválení denního výkazu — Směna | Místo; logika úprav po zakázkách beze změny.
          355: na počítači panel zprava, ↑ ↓ další záznam fronty */}
      <ReviewPanel open={!!dayEdit} onClose={() => setDayEdit(null)} nav={nav} dirty={dayDirty} testid="approval-day-edit-dialog" mobileClass="max-w-[680px]"
        onInteractOutside={(e) => { if (lightbox || pinView || del) e.preventDefault(); }}>
          {(controls) => dayEdit && (() => {
            const rowsBreak = dayEdit.rows.reduce((a, r) => a + (Number(r.break_minutes) || 0), 0);
            const alloc = dayEdit.rows.reduce((a, r) => a + Math.max(0, (toMin(r.t_to) - toMin(r.t_from)) - (Number(r.break_minutes) || 0)), 0);
            const tracked = Math.max(0, Math.round((new Date(dayEdit.endedAt) - new Date(dayEdit.startedAt)) / 60000) - rowsBreak);
            const diff = alloc - tracked;
            const nearest = (pt) => {
              if (!pt || !dayEdit.sites.length) return {};
              const best = dayEdit.sites.map((st) => ({ st, d: haversineM(pt, st) })).sort((x, y) => x.d - y.d)[0];
              return { distance_m: Math.round(best.d), out: best.d > radius, siteName: dayEdit.sites.length > 1 ? best.st.name : null };
            };
            const dayApproved = dayEdit.rows.length > 0 && dayEdit.rows.every((r) => r.approval);
            const gs = dayEdit.geo?.start, ge = dayEdit.geo?.end;
            const outAny = [gs, ge].some((pt) => pt && nearest(pt).out);
            return (<>
              <DialogHeader className="pl-4 sm:pl-6 pr-14 md:pr-6 pt-1 md:pt-5 pb-3 space-y-0 text-left">
                {controls && (   // 358: na počítači Schválit vlevo nahoře, vpravo 2 / 5 ↑ ↓ ✕
                  <div className="flex items-center gap-2 !mb-3" data-testid="day-top">
                    <ApproveButton done={dayApproved} disabled={busy} onClick={dayApproved ? unapproveDay : (e) => submitDayEdit(e, true)}
                      label={dayApproved ? "Schváleno" : dayEdit.rows.filter((x) => !x.approval).length > 1 ? (dayDirty ? "Upravit a schválit vše" : "Schválit vše") : (dayDirty ? "Upravit a schválit" : "Schválit")}
                      title={dayApproved ? "Zrušit schválení" : undefined} testid={dayApproved ? "approval-day-approved" : "approval-day-approve"} />
                    <span className="flex-1" />{controls}
                  </div>
                )}
                <div className="min-w-0">
                  <DialogTitle className="text-xl sm:text-2xl font-black tracking-tight truncate">{dayEdit.userName}</DialogTitle>
                  <p className="text-sm text-muted-foreground truncate">Denní výkaz · {formatDate(dayEdit.date)}</p>
                </div>
                <div ref={autoPill} role="tablist" className="!mt-3 -mr-10 md:mr-0 grid grid-cols-2 p-1 rounded-full bg-secondary text-[14px] font-bold" data-testid="day-tabs">
                  {[["shift", "Směna"], ["place", <>Místo{outAny ? <span className="text-amber-600 dark:text-amber-400"> ⚠</span> : null}</>]].map(([k, label]) => (
                    <button key={k} role="tab" type="button" aria-selected={dayTab === k} onClick={() => setDayTab(k)} data-testid={`day-tab-${k}`}
                      className={`h-9 rounded-full transition-colors ${dayTab === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>{label}</button>
                  ))}
                </div>
              </DialogHeader>

              <div className="overflow-y-auto overscroll-contain px-4 sm:px-6 pb-4 space-y-3">
                {dayTab === "place" ? (
                  <PresenceMap testid="day-place" radius={radius} sites={dayEdit.sites}
                    points={[
                      { key: "start", letter: "Z", color: "#1f8a5b", label: "Začátek", time: formatTime(dayEdit.startedAt), lat: gs?.lat, lng: gs?.lng, ...nearest(gs) },
                      { key: "end", letter: "K", color: "#c77a12", label: "Konec", time: formatTime(dayEdit.endedAt), lat: ge?.lat, lng: ge?.lng, ...nearest(ge),
                        note: dayEdit.autoClosed ? "auto odhlášeno" : null },
                    ]} />
                ) : (<>
                  <p className="text-[13px] text-muted-foreground tabular-nums">naměřeno {formatTime(dayEdit.startedAt)}–{formatTime(dayEdit.endedAt)} · přestávka {rowsBreak} min</p>
                  <div className={`rounded-[12px] px-3 py-2.5 text-sm font-bold ${Math.abs(diff) <= 15 ? "bg-success/10 text-success" : "bg-brand/10 text-brand"}`} data-testid="day-edit-diff">
                    {Math.abs(diff) <= 15 ? "Rozdělení sedí s naměřeným časem ✓" : diff > 0 ? `Rozděleno o ${Math.floor(diff / 60)}:${String(diff % 60).padStart(2, "0")} h víc, než je naměřeno` : `Zbývá rozdělit ${Math.floor(-diff / 60)}:${String((-diff) % 60).padStart(2, "0")} h`}
                  </div>
                  {dayEdit.rows.map((r, i) => {
                    const setRow = (k, v) => setDayEdit((d) => ({ ...d, rows: d.rows.map((x, xi) => xi === i ? { ...x, [k]: v } : x) }));
                    const init = JSON.parse(dayEdit.orig)[i] || [];
                    const ring = (on) => on ? "ring-2 ring-brand/40 rounded-[12px]" : "";
                    const photos = r.photos || [];
                    const pendingCount = dayEdit.rows.filter((x) => !x.approval).length;
                    if (r.approval) {
                      return (
                        <section key={i} className="rounded-[18px] border border-success/40 bg-success/[0.06] p-3.5 flex items-center gap-3 min-w-0" data-testid={`day-edit-row-${i}`}>
                          <Check size={18} className="text-success shrink-0" />
                          <div className="min-w-0 flex-1"><div className="font-extrabold text-[15px] truncate">{r.projectName || "Bez zakázky"}</div>
                            <div className="text-[13px] text-muted-foreground tabular-nums">{r.t_from}–{r.t_to} · schválil(a) {r.approval.approved_by_name}</div></div>
                        </section>
                      );
                    }
                    return (
                      <section key={i} className="rounded-[18px] border border-border bg-secondary/40 p-3.5 space-y-3 min-w-0 [&_input]:bg-card [&_textarea]:bg-card" data-testid={`day-edit-row-${i}`}>
                        <h3 className="font-extrabold text-[15px] truncate">{r.projectName || "Bez zakázky"}</h3>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="min-w-0"><Label className="text-xs text-muted-foreground">Od</Label><div className={`mt-1 ${ring(r.t_from !== init[0])}`}><TimeField value={r.t_from} onChange={(v) => setRow("t_from", v)} testid={`day-edit-from-${i}`} label="Od" /></div></div>
                          <div className="min-w-0"><Label className="text-xs text-muted-foreground">Do</Label><div className={`mt-1 ${ring(r.t_to !== init[1])}`}><TimeField value={r.t_to} onChange={(v) => setRow("t_to", v)} testid={`day-edit-to-${i}`} label="Do" /></div></div>
                          <div className="min-w-0"><Label className="text-xs text-muted-foreground">Přestávka</Label><div className="mt-1"><BreakChips compact value={String(Number(r.break_minutes) || 0)} onChange={(v) => setRow("break_minutes", v)} testid={`day-edit-break-${i}`} /></div></div>
                        </div>
                        <div>
                          <Label className="text-[11px] font-extrabold uppercase tracking-[.08em] text-muted-foreground">Co dělal</Label>
                          <Textarea rows={2} value={r.note || ""} onChange={(e) => setRow("note", e.target.value)} placeholder="Bez popisu"
                            className={`mt-1 w-full min-w-0 ${(r.note || "").trim() !== init[3] ? "border-brand ring-2 ring-brand/30" : ""}`} data-testid={`day-edit-note-${i}`} />
                        </div>
                        <div>
                          <div className="text-[11px] font-extrabold uppercase tracking-[.08em] text-muted-foreground mb-1.5">Fotky · {photos.length}</div>
                          {photos.length ? (
                            <div className="flex flex-wrap gap-2" data-testid={`day-edit-photos-${i}`}>
                              {photos.map((url, pi) => (
                                <button key={pi} type="button" onClick={() => setPinView({ row: i, start: pi })} aria-label="Zvětšit fotku" data-testid={`day-photo-${i}-${pi}`}
                                  className="relative w-[72px] h-[72px] rounded-[12px] overflow-hidden border border-border shrink-0">
                                  <SignedImg url={url} alt="" thumb={72} className="w-full h-full object-cover" />
                                  {(rowDrafts[i] || []).filter((d) => d.photo_url === url).length > 0 && (
                                    <span className="absolute right-1 bottom-1 min-w-[20px] h-[20px] px-1 rounded-full bg-brand text-white text-[10.5px] font-extrabold grid place-items-center border-2 border-white">
                                      {(rowDrafts[i] || []).filter((d) => d.photo_url === url).length}</span>
                                  )}
                                </button>
                              ))}
                            </div>
                          ) : <p className="text-[13px] text-muted-foreground">Žádné fotky.</p>}
                          {photos.length > 0 && (
                            <button type="button" onClick={() => setPinView({ row: i, start: 0 })} data-testid={`day-defect-${i}`}
                              className="mt-2 inline-flex items-center gap-1 text-[13px] font-bold text-brand hover:underline"><MapPin size={14} /> Označit závadu → úkol</button>
                          )}
                        </div>
                        <DefectTaskForm drafts={rowDrafts[i] || []} setDrafts={draftsSetter(i)} projectId={r.project_id} projectName={r.projectName}
                          assignee={{ id: dayEdit.userId, name: dayEdit.userName }} sourceKind="attendance" sourceId={dayEdit.id}
                          onEditDraft={(k) => setPinView({ row: i, start: Math.max(0, photos.indexOf((rowDrafts[i] || [])[k]?.photo_url)), focusDraft: k })}
                          note="Výkaz schválíte zvlášť. Úkol dostane fotky se špendlíky a popisy." />
                        {(r.tasks || []).length > 0 && (
                          <div data-testid={`day-edit-tasks-${i}`}>
                            <div className="text-[11px] font-extrabold uppercase tracking-[.08em] text-muted-foreground mb-1">Hotové úkoly</div>
                            {r.tasks.map((t) => <div key={t.id} className="flex items-center gap-2 text-sm"><Check size={15} className="text-success shrink-0" /><span className="truncate">{t.title}</span></div>)}
                          </div>
                        )}
                        {rowDirty(i) && (
                          <div className="rounded-[14px] border-[1.5px] border-amber-500/70 bg-amber-500/[0.07] p-3" data-testid={`day-edit-reason-box-${i}`}>
                            <Label className="text-[13px] font-bold">Důvod úpravy u této zakázky <span className="text-destructive">*</span></Label>
                            <Input value={r.reason || ""} onChange={(e) => setRow("reason", e.target.value)} placeholder="Např. odešel dřív, přestávka nebyla odečtena…"
                              className="h-11 mt-1.5 w-full min-w-0" data-testid={`day-edit-reason-${i}`} />
                            <p className="text-[12px] text-muted-foreground mt-1.5">Pracovník důvod uvidí.</p>
                          </div>
                        )}
                        {pendingCount > 1 && (
                          <div className="flex md:justify-end">
                            <Button onClick={() => approveRowUi(i)} disabled={busy} variant="outline" data-testid={`day-row-approve-${i}`}
                              className="h-11 w-full md:w-auto rounded-full font-extrabold border-success/50 text-success hover:bg-success/10"><Check size={16} /> {rowDirty(i) ? "Upravit a schválit tuto zakázku" : "Schválit tuto zakázku"}</Button>
                          </div>
                        )}
                      </section>
                    );
                  })}
                </>)}
              </div>

              <div className="border-t border-border bg-card px-4 sm:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex flex-col-reverse md:flex-row md:justify-between gap-2">
                <Button variant="outline" onClick={() => setDel({ kind: "day", id: dayEdit.id, label: `${dayEdit.userName} · ${formatDate(dayEdit.date)}` })} disabled={busy}
                  data-testid="approval-day-delete" className="h-11 md:h-12 w-full md:w-auto rounded-full text-destructive font-bold"><Trash2 size={16} /> Smazat výkaz</Button>
                {!controls && (   // na počítači je Schválit nahoře
                  <Button onClick={() => submitDayEdit()} disabled={busy} data-testid="approval-day-edit-save"
                    className="h-[52px] md:h-12 w-full md:w-auto rounded-full font-extrabold text-[15px] px-8 bg-success hover:bg-success/90 text-white">
                    <Check size={18} /> {dayEdit.rows.filter((x) => !x.approval).length > 1 ? (dayDirty ? "Upravit a schválit vše" : "Schválit vše") : (dayDirty ? "Upravit a schválit" : "Schválit")}
                  </Button>
                )}
              </div>
            </>);
          })()}
      </ReviewPanel>
      <PhotoLightbox url={lightbox} onClose={() => setLightbox(null)} />
      <PhotoPinViewer open={!!pinView && !!dayEdit} onClose={() => setPinView(null)}
        photos={(pinView && dayEdit?.rows[pinView.row]?.photos) || []} start={pinView?.start || 0} focusDraft={pinView?.focusDraft ?? null}
        title={dayEdit && pinView ? `${dayEdit.userName} · ${dayEdit.rows[pinView.row]?.projectName || "Bez zakázky"}` : ""}
        drafts={pinView ? rowDrafts[pinView.row] || [] : []} setDrafts={pinView ? draftsSetter(pinView.row) : () => {}}
        editable={!!dayEdit?.rows[pinView?.row]?.project_id} onDone={() => setPinView(null)} />

      <AlertDialog open={!!del} onOpenChange={(o) => { if (!o) setDel(null); }}>
        <AlertDialogContent data-testid="approval-del-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat záznam?</AlertDialogTitle>
            <AlertDialogDescription>{del?.label} — {del?.kind === "day" ? "celý denní výkaz včetně rozdělení na zakázky" : "směna"} se nevratně smaže a nebude ve mzdách. Fotky v deníku zůstanou.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setDel(null)} className="h-11 w-full sm:w-auto">Zpět</Button>
            <Button variant="destructive" onClick={runDelete} disabled={busy} className="h-11 w-full sm:w-auto font-bold" data-testid="approval-del-confirm">Smazat</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShiftReviewDialog sid={reviewSid} mode="approve" canSeeMoney={canSeeMoney} nav={nav}
        onClose={() => setReviewSid(null)} onChanged={panelChanged} onApproval={(ok) => markOk(reviewSid, "seg", ok)} />
    </>
  );
  if (pass) return <>{passCard}{dialogs}</>;
  if (variant === "band") return <>{bandPanel}{dialogs}</>;
  if (rows === null || days === null || total === 0) return null;

  return (
    <div className="bg-card border border-brand/40 rounded-[16px] p-4 md:p-5" data-testid="approval-queue">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h2 className="text-[17px] font-bold tracking-tight flex items-center gap-2">
          <ClipboardCheck size={19} className="text-brand" /> Docházka k odsouhlasení
          <span className="rounded-full bg-brand/15 text-brand px-2.5 py-0.5 text-xs font-bold tabular-nums" data-testid="approval-queue-count">{total}</span>
        </h2>
        <Button onClick={() => { setOnlyClean(true); setConfirmAll(true); }} disabled={busy} data-testid="approval-approve-all"
          className="h-[34px] rounded-[10px] bg-success hover:bg-success/90 text-white font-bold text-[13px]">
          <Check size={14} /> Schválit vše
        </Button>
      </div>
      <p className="text-[13px] text-muted-foreground mb-4 tabular-nums" data-testid="approval-header-summary">
        {total} {plural(total)} · {formatHours(totalHours)}{showCost ? ` · ${formatCZK(totalCost)}` : ""}{warnCount > 0 ? ` · ${warnCount} s varováním` : ""}
      </p>

      {/* desktop */}
      <div className="hidden md:block space-y-2">
        {rows.map((r) => (
          <div key={r.id} onClick={() => setReviewSid(r.id)} data-testid={`approval-seg-${r.id}`}
            className="group flex items-center gap-4 rounded-[12px] border border-border px-3.5 py-2.5 cursor-pointer hover:border-brand/50 hover:bg-secondary/40 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{r.user_name}</span>
                {r.project_name && <span className="text-muted-foreground text-sm truncate">· {r.project_name}</span>}
                <QueueFlags flags={r.flags} hours={r.hours} />
              </div>
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-[13px] text-muted-foreground tabular-nums">{formatDate(r.date)} · {formatTime(r.check_in)}–{r.check_out ? formatTime(r.check_out) : "…"}</span>
                <RowIndicators r={r} />
              </div>
            </div>
            <span className={`font-bold tabular-nums shrink-0 ${(r.flags || []).includes("short") ? "text-destructive" : ""}`}>{formatHours(r.hours)}</span>
            {showCost && segCost(r) != null && <span className="text-sm text-muted-foreground tabular-nums shrink-0 w-20 text-right">{formatCZK(segCost(r))}</span>}
            {trash({ kind: "seg", id: r.id, label: `${r.user_name} · ${formatDate(r.date)}` })}
            <button onClick={(e) => { e.stopPropagation(); approveSeg(r.id); }} disabled={busy} data-testid={`approval-ok-${r.id}`} title="Schválit rovnou"
              className="inline-flex items-center justify-center h-8 w-8 rounded-[8px] bg-success text-white shrink-0"><Check size={15} /></button>
          </div>
        ))}
        {days.map((it) => (
          <div key={it.id} onClick={() => openDayEdit(it)} data-testid={`approval-day-${it.id}`} title="Otevřít a upravit výkaz"
            className="group flex items-center gap-4 rounded-[12px] border border-border px-3.5 py-2.5 cursor-pointer hover:border-brand/50 hover:bg-secondary/40 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{it.userName}</span>
                <span className="rounded-full bg-secondary text-muted-foreground px-2 py-0.5 text-[11px] font-bold uppercase">výkaz</span>
                {it.autoClosed && <span className="text-[11px] font-bold uppercase text-destructive bg-destructive/10 rounded-md px-1.5 py-0.5">auto odhlášeno</span>}
                {!it.geo?.start && <span className="text-[11px] font-bold uppercase text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded-md px-1.5 py-0.5" data-testid={`day-no-gps-${it.id}`}>bez GPS</span>}
                {it.balance === "overtime" && <span className="text-[11px] font-bold uppercase text-brand bg-brand/10 rounded-md px-1.5 py-0.5">přesčas</span>}
              </div>
              <div className="text-[13px] text-muted-foreground tabular-nums">{formatDate(it.date)}{(it.allocations || []).length ? ` · ${it.allocations.map((a) => a.projectName || "Bez zakázky").join(" · ")}` : ""}</div>
            </div>
            <span className="font-bold tabular-nums shrink-0">{formatHours(it.trackedHours)}</span>
            {showCost && it.cost != null && <span className="text-sm text-muted-foreground tabular-nums shrink-0 w-20 text-right">{formatCZK(it.cost)}</span>}
            {trash({ kind: "day", id: it.id, label: `${it.userName} · ${formatDate(it.date)}` })}
            <button onClick={(e) => { e.stopPropagation(); approveDay(it.id); }} disabled={busy} data-testid={`approval-day-ok-${it.id}`} title="Schválit rovnou"
              className="inline-flex items-center justify-center h-8 w-8 rounded-[8px] bg-success text-white shrink-0"><Check size={15} /></button>
          </div>
        ))}
      </div>

      {/* mobil */}
      <div className="md:hidden">
        {!mobileOpen ? (
          <div className="rounded-[12px] border border-border px-3.5 py-3 flex items-center justify-between gap-3">
            <span className="text-sm"><b className="tabular-nums">{total}</b> {plural(total)} · <b className="tabular-nums">{formatHours(totalHours)}</b>{showCost ? <> · <span className="tabular-nums">{formatCZK(totalCost)}</span></> : null}</span>
            <Button onClick={() => setMobileOpen(true)} className="h-11 font-bold shrink-0" variant="outline" data-testid="approval-mobile-open">Zobrazit</Button>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} onClick={() => setReviewSid(r.id)} className="rounded-[12px] border border-border px-3.5 py-3" data-testid={`approval-seg-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{r.user_name}</span>
                  {r.project_name && <span className="text-sm text-muted-foreground truncate">· {r.project_name}</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-1"><QueueFlags flags={r.flags} hours={r.hours} /></div>
                <div className="flex items-center justify-between gap-3 mt-1.5">
                  <span className="text-[13px] text-muted-foreground tabular-nums">{formatDate(r.date)} · {formatTime(r.check_in)}–{r.check_out ? formatTime(r.check_out) : "…"}</span>
                  <RowIndicators r={r} />
                </div>
                <div className="flex items-center justify-between gap-3 mt-2">
                  <span className={`font-bold tabular-nums ${(r.flags || []).includes("short") ? "text-destructive" : ""}`}>{formatHours(r.hours)}{showCost && segCost(r) != null ? ` · ${formatCZK(segCost(r))}` : ""}</span>
                  <span className="flex items-center gap-2">{trash({ kind: "seg", id: r.id, label: `${r.user_name} · ${formatDate(r.date)}` }, 9)}
                  <button onClick={(e) => { e.stopPropagation(); approveSeg(r.id); }} disabled={busy} data-testid={`approval-ok-${r.id}`}
                    className="inline-flex items-center justify-center h-9 w-9 rounded-[8px] bg-success text-white"><Check size={16} /></button></span>
                </div>
              </div>
            ))}
            {days.map((it) => (
              <div key={it.id} onClick={() => openDayEdit(it)} className="rounded-[12px] border border-border px-3.5 py-3" data-testid={`approval-day-${it.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{it.userName}</span>
                  <span className="rounded-full bg-secondary text-muted-foreground px-2 py-0.5 text-[11px] font-bold uppercase">výkaz</span>
                  {it.autoClosed && <span className="text-[11px] font-bold uppercase text-destructive bg-destructive/10 rounded-md px-1.5 py-0.5">auto odhlášeno</span>}
                {!it.geo?.start && <span className="text-[11px] font-bold uppercase text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded-md px-1.5 py-0.5" data-testid={`day-no-gps-${it.id}`}>bez GPS</span>}
                </div>
                <div className="text-[13px] text-muted-foreground tabular-nums mt-0.5">{formatDate(it.date)}</div>
                <div className="flex items-center justify-between gap-3 mt-2">
                  <span className="font-bold tabular-nums">{formatHours(it.trackedHours)}{showCost && it.cost != null ? ` · ${formatCZK(it.cost)}` : ""}</span>
                  <span className="flex items-center gap-2">{trash({ kind: "day", id: it.id, label: `${it.userName} · ${formatDate(it.date)}` }, 9)}
                  <button onClick={(e) => { e.stopPropagation(); approveDay(it.id); }} disabled={busy} data-testid={`approval-day-ok-${it.id}`}
                    className="inline-flex items-center justify-center h-9 w-9 rounded-[8px] bg-success text-white"><Check size={16} /></button></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {dialogs}
    </div>
  );
}
